import { useMutation, useQuery } from '@tanstack/react-query'
import { type OfferChannel, OfferStatus } from '@today/types'
import { type ChannelMismatchWarningProps, OfferSendStepState, type OfferSendStepViewProps } from '@today/ui-domain'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { OFFERS_NAMESPACE } from '@/i18n'
import { useChannelMismatchWarning } from '@/modules/offer-notification/useChannelMismatchWarning'
import { channelEligibility } from '@/modules/offer-wizard/channel-eligibility'
import { browserTimeZone, dateInZone, endOfDayInZone, expiryRange } from '@/modules/offer-wizard/expiry-date'
import { type SendRefusal, SendRefusalKind, sendRefusalOf } from '@/modules/offer-wizard/send-refusal'
import { OFFER_WIZARD_STEPS } from '@/pages/Offers/offer-wizard-steps'
import { customerService } from '@/services/customer.service'
import { type OfferDTO, offerKeys, offerService } from '@/services/offer.service'

const ONCE = { retry: false, refetchOnWindowFocus: false } as const

/** The four reads Step 4 makes on load — each exactly once (TO-125 NFR). */
function useSendStepReads(offerId: string) {
	const offer = useQuery({ ...ONCE, queryKey: offerKeys.detail(offerId), queryFn: () => offerService.get(offerId) })
	const customerId = offer.data?.customerId
	const forCustomer = { ...ONCE, enabled: customerId !== undefined }
	const channels = useQuery({
		...ONCE,
		queryKey: ['offers', 'send-channels'],
		queryFn: () => offerService.sendChannels(),
	})
	const customer = useQuery({
		...forCustomer,
		queryKey: ['offer-send', 'customer', customerId],
		queryFn: () => customerService.get(customerId as string),
	})
	const consents = useQuery({
		...forCustomer,
		queryKey: ['offer-send', 'consents', customerId],
		queryFn: () => customerService.consents(customerId as string),
	})
	return { offer, channels, customer, consents }
}

/**
 * The expiry field (TO-125 REQ-105): the stored expiry as a date in the user's
 * zone, a change inside today…today+364, or a clear. `toPatch` is the PATCH
 * value when it changed, else undefined.
 */
function useExpiryField(offer: OfferDTO | undefined, zone: string) {
	const { i18n } = useTranslation(OFFERS_NAMESPACE)
	const [edited, setEdited] = useState<string | null>(null)
	const stored = offer?.expiresAt ? dateInZone(new Date(offer.expiresAt), zone) : ''
	const value = edited ?? stored
	const range = expiryRange(new Date(), zone)
	const instant = value === '' ? null : endOfDayInZone(value, zone)
	return {
		field: {
			value,
			min: range.min,
			max: range.max,
			display: instant ? instant.toLocaleDateString(i18n.language, { timeZone: zone }) : null,
		},
		toPatch: value === stored ? undefined : (instant?.toISOString() ?? null),
		onChange: (next: string) => {
			if (next !== '' && next >= range.min && next <= range.max) setEdited(next)
		},
		onClear: () => setEdited(''),
	}
}

/** The ticked channels, toggled one at a time. */
function useChannelChoice() {
	const [chosen, setChosen] = useState<ReadonlySet<OfferChannel>>(new Set())
	const toggle = (channel: OfferChannel) =>
		setChosen((current) => {
			const next = new Set(current)
			if (next.has(channel)) next.delete(channel)
			else next.add(channel)
			return next
		})
	return { chosen, toggle }
}

/** The recipient, each channel's eligibility, and the ticked channels that are eligible. */
function selectableChannels(reads: ReturnType<typeof useSendStepReads>, chosen: ReadonlySet<OfferChannel>) {
	const person = reads.customer.data
	const contacts = person ? { email: person.primaryEmail !== null, mobile: person.primaryMobile !== null } : null
	const options = channelEligibility(reads.channels.data ?? [], reads.consents.data?.current ?? null, contacts)
	const selected = options.filter((o) => o.enabled && chosen.has(o.channel)).map((o) => o.channel)
	return { person, options, selected }
}

/** A refusal worded for the view, with links back to the steps that fix it. */
function useRefusalView(offerId: string, refusal: SendRefusal | null) {
	const { t } = useTranslation(OFFERS_NAMESPACE)
	if (!refusal) return null
	const channels = refusal.channels.map((c) => t(`send.channels.${c}`)).join(', ')
	const links = refusal.steps.flatMap((id) => {
		const step = OFFER_WIZARD_STEPS.find((s) => s.id === id)
		return step ? [{ label: t(`wizard.steps.${id}`), href: step.path(offerId) }] : []
	})
	if (refusal.kind === SendRefusalKind.CONFLICT)
		links.push({ label: t('send.refusal.openOffer'), href: `/offers/${encodeURIComponent(offerId)}` })
	return { message: t(`send.refusal.${refusal.kind}`, { channels }), links }
}

/**
 * Confirm → save a changed expiry (PATCH), then send; once (a double confirm
 * sends once); on `202` the detail page; on a refusal the summary closes and
 * the refusal shows.
 */
function useConfirmSend(offerId: string, run: () => Promise<unknown>, onRefused: (error: unknown) => void) {
	const navigate = useNavigate()
	const inFlight = useRef(false)
	const mutation = useMutation({
		mutationFn: run,
		onSuccess: () => void navigate(`/offers/${encodeURIComponent(offerId)}`),
		onError: onRefused,
		onSettled: () => {
			inFlight.current = false
		},
	})
	const confirm = () => {
		if (inFlight.current) return
		inFlight.current = true
		mutation.mutate()
	}
	return { confirm, isPending: mutation.isPending }
}

/** A non-DRAFT offer has nothing to send: go to its detail page (TO-125 REQ-102). */
function useLeaveUnlessDraft(offer: OfferDTO | undefined, offerId: string) {
	const navigate = useNavigate()
	useEffect(() => {
		if (offer && offer.status !== OfferStatus.DRAFT)
			void navigate(`/offers/${encodeURIComponent(offerId)}`, { replace: true })
	}, [offer, offerId, navigate])
}

/**
 * Offer wizard Step 4 (TO-125 offer-wizard-send-step REQ-102..REQ-107): reads the
 * draft, the tenant's enabled channels and the recipient's contacts and consents
 * (all settled before the picker shows); disables the channels the recipient
 * cannot receive; edits the expiry in the user's time zone; on Confirm saves a
 * changed expiry and sends, then opens the detail page where the progress shows.
 * A linked template written for an unselected channel holds Send until confirmed (TO-140).
 */
export function useOfferSendStep(
	offerId: string,
): Omit<OfferSendStepViewProps, 't' | 'messageWarning'> & { messageWarning: ChannelMismatchWarningProps | null } {
	const zone = browserTimeZone()
	const reads = useSendStepReads(offerId)
	useLeaveUnlessDraft(reads.offer.data, offerId)
	const expiry = useExpiryField(reads.offer.data, zone)
	const { chosen, toggle } = useChannelChoice()
	const [confirmOpen, setConfirmOpen] = useState(false)
	const [refusal, setRefusal] = useState<SendRefusal | null>(null)

	const { person, options, selected } = selectableChannels(reads, chosen)
	const mismatch = useChannelMismatchWarning(offerId, reads.offer.data?.notificationTemplateId ?? null, selected)

	const send = useConfirmSend(
		offerId,
		async () => {
			if (expiry.toPatch !== undefined) await offerService.update(offerId, { expiresAt: expiry.toPatch })
			return offerService.send(offerId, selected)
		},
		(error) => {
			setConfirmOpen(false)
			setRefusal(sendRefusalOf(error))
		},
	)

	const pending = [reads.offer, reads.channels, reads.customer, reads.consents].some((q) => q.isPending)
	const failed = reads.offer.isError || reads.channels.isError
	return {
		state: failed ? OfferSendStepState.ERROR : pending ? OfferSendStepState.LOADING : OfferSendStepState.READY,
		channels: options.map((o) => ({ ...o, selected: selected.includes(o.channel) })),
		consentNotice: reads.consents.isError || reads.customer.isError,
		expiry: expiry.field,
		sendDisabled: selected.length === 0 || send.isPending || mismatch.blocksSend,
		confirmOpen,
		recipientName: person?.fullName ?? '',
		isSending: send.isPending,
		refusal: useRefusalView(offerId, refusal),
		messageWarning: mismatch.warning,
		onToggleChannel: (channel: OfferChannel) => {
			mismatch.reset()
			toggle(channel)
		},
		onExpiryChange: expiry.onChange,
		onExpiryClear: expiry.onClear,
		onSend: () => {
			setRefusal(null)
			setConfirmOpen(true)
		},
		onConfirm: send.confirm,
		onCancel: () => setConfirmOpen(false),
	}
}
